import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {useIntl} from 'react-intl';
import {useHistory} from 'react-router-dom';
import moment, {Moment} from 'moment';
import {ComboBox, Detail, Modal, Text, TextField} from '@forecast-it/design-system';
import InlineLoader from '../../../forecast-app/shared/components/inline-loader/inline_loader';
import {MODAL_TYPE, showModal} from '../../../forecast-app/shared/components/modals/generic_modal_conductor';
import {hasPermission} from '../../../forecast-app/shared/util/PermissionsUtil';
import {PERMISSION_TYPE} from '../../../Permissions';
import * as tracking from '../../../tracking';
import {trackEvent} from '../../../tracking/amplitude/TrackingV2';
import {
	fetchUnlinkedXeroInvoices,
	fetchXeroOrganisations,
	XeroOrganisation,
} from '../../../components/insights/invoicing/importFromXero';
import {
	DateInput,
	FilterField,
	FilterRow,
	ListArea,
	LoadingArea,
	OrganisationInput,
	SelectionNote,
	VisuallyHidden,
} from './XeroInvoiceImportModal.styled';
import {ImportDateRange, InvoiceDateRange} from './ImportDateRange';
import {organisationMessage, UnlinkedInvoiceList, UnlinkedListState} from './UnlinkedInvoiceList';
import {
	headerState,
	invoiceKey,
	refreshSelection,
	SelectableInvoice,
	SelectedInvoices,
	selectionCounts,
	toggleInvoice,
	toggleVisible,
} from './invoiceSelection';
import {
	canSelectFor,
	ContactPair,
	contactKey,
	contactsToAsk,
	decorate,
	effectiveMatches,
	fetchInvoiceClientMatches,
	isKept,
	KnownMatches,
	MAX_CONTACTS_PER_REQUEST,
	matchDisplay,
} from './clientMatch';
import {allDone, failedSummary, ImportSummary, notCreatedCount, postImport, rowsToImport, runImport} from './importInvoices';

/*
 * The Import Invoices and Payments from Xero modal, opened from All invoices. It lists the Xero sales
 * invoices dated in the chosen range that no Forecast invoice links to, to tick for import. Ticks are
 * kept across range changes; Create imports the ticked invoices matched to a client (T4-216). Choosing
 * an organisation comes later. Each row's Client match is asked after its list loads, per Xero
 * contact. Everything it holds lives here, so each open starts fresh with nothing ticked or matched.
 */

const ALL_ORGANISATIONS = 'all';
const XERO_INTEGRATION_PAGE = '/admin/integrations/xero';

/** The default Invoice date range: the month ending today. ISO dates. */
export const defaultInvoiceDateRange = (today: Moment): {startDate: string; endDate: string} => ({
	startDate: today.clone().subtract(1, 'month').format('YYYY-MM-DD'),
	endDate: today.clone().format('YYYY-MM-DD'),
});

/** What the modal is opened with. onImported: after a Create run, with every invoice's outcome. */
export type XeroInvoiceImportModalPayload = {onImported?: (summary: ImportSummary) => void};

/** Opens the modal through the generic modal conductor. */
export const showXeroInvoiceImportModal = (props: XeroInvoiceImportModalPayload = {}): void => {
	showModal({type: MODAL_TYPE.XERO_INVOICE_IMPORT, ...props});
};

type Props = XeroInvoiceImportModalPayload & {
	closeModal: () => void;
};

const XeroInvoiceImportModal = ({closeModal, onImported}: Props): JSX.Element | null => {
	const {formatMessage} = useIntl();
	const history = useHistory();
	// Organisations Forecast can reach; null while they load.
	const [organisations, setOrganisations] = useState<XeroOrganisation[] | null>(null);
	// Every connected organisation's name, reachable or not, for the lines about ones not listed.
	const [organisationNames, setOrganisationNames] = useState<Map<string, string>>(() => new Map());
	const [dateRange, setDateRange] = useState<InvoiceDateRange>(() => defaultInvoiceDateRange(moment()));
	const [list, setList] = useState<UnlinkedListState>({state: 'loading'});
	// The ticked invoices, each with a copy of its row, from any range. What Create hands on later.
	const [selected, setSelected] = useState<SelectedInvoices>(() => new Map());
	const open = useRef(true);
	// Only the newest request's answer is used: a slow answer for an older range must not replace it.
	const latestRequest = useRef(0);
	const isAdminUser = useMemo(() => hasPermission(PERMISSION_TYPE.MANAGE_ACCOUNT_SETTINGS), []);
	const hasClientCreate = useMemo(() => hasPermission(PERMISSION_TYPE.CLIENT_CREATE), []);
	// The Client match answers, per contact, for this open. The ref is what the asking loop reads.
	const [known, setKnownState] = useState<KnownMatches>(() => new Map());
	const knownRef = useRef<KnownMatches>(known);
	const setKnown = useCallback((update: (current: KnownMatches) => KnownMatches) => {
		knownRef.current = update(knownRef.current);
		setKnownState(knownRef.current);
	}, []);
	// Contacts waiting to be asked, newest list first, and whether a request is out. One at a time.
	const toAsk = useRef<ContactPair[]>([]);
	const asking = useRef(false);
	// The contacts of the request that is out: a list loading meanwhile must not queue them again.
	const inFlight = useRef<ReadonlySet<string>>(new Set());
	// A Create run is out (D3): the controls wait for it.
	const [running, setRunning] = useState(false);
	// How many invoices the last run could not import, for the line next to Cancel (D5).
	const [notCreated, setNotCreated] = useState(0);

	const askNextBatch = useCallback(async () => {
		asking.current = true;
		while (open.current && toAsk.current.length > 0) {
			const batch = toAsk.current.slice(0, MAX_CONTACTS_PER_REQUEST);
			toAsk.current = toAsk.current.slice(MAX_CONTACTS_PER_REQUEST);
			inFlight.current = new Set(batch.map(contactKey));
			const answers = await fetchInvoiceClientMatches(batch);
			inFlight.current = new Set();
			if (!open.current) {
				break;
			}
			const byKey = new Map((answers || []).map(answer => [contactKey(answer), answer]));
			setKnown(current => {
				const next = new Map(current);
				batch.forEach(contact => {
					const key = contactKey(contact);
					const answer = byKey.get(key);
					if (answer) {
						next.set(key, answer);
					} else if (!isKept(current.get(key))) {
						// A failed request, or a contact the answer leaves out, is unknown: asked again on the
						// next load. It never replaces a kept answer, whose row may be ticked already.
						next.set(key, {...contact, match: 'unknown'});
					}
				});
				return next;
			});
		}
		asking.current = false;
	}, [setKnown]);

	/** Queues this list's contacts with no kept answer, ahead of older lists, and starts asking. */
	const askMatches = useCallback(
		(rows: SelectableInvoice[]) => {
			const fresh = contactsToAsk(rows, knownRef.current).filter(contact => !inFlight.current.has(contactKey(contact)));
			const freshKeys = new Set(fresh.map(contactKey));
			toAsk.current = [...fresh, ...toAsk.current.filter(contact => !freshKeys.has(contactKey(contact)))];
			// An unknown asked again shows the skeleton until its new answer.
			if (fresh.some(contact => knownRef.current.has(contactKey(contact)))) {
				setKnown(current => {
					const next = new Map(current);
					freshKeys.forEach(key => next.delete(key));
					return next;
				});
			}
			if (!asking.current) {
				askNextBatch();
			}
		},
		[askNextBatch, setKnown]
	);

	const showReconnectWindow = useCallback(() => {
		closeModal();
		showModal({type: MODAL_TYPE.XERO_INACCESSIBLE, isAdminUser});
	}, [closeModal, isAdminUser]);

	const loadInvoices = useCallback(
		(range: InvoiceDateRange) => {
			const request = ++latestRequest.current;
			setList({state: 'loading'});
			fetchUnlinkedXeroInvoices(range).then(data => {
				if (!open.current || request !== latestRequest.current) {
					return;
				}
				if (data === null) {
					setList({state: 'error'});
					return;
				}
				if (data.organisations.every(org => org.status === 'inaccessible')) {
					// Every connection expired or went since the modal opened (none left counts too): as on
					// open, the reconnect window takes over, never "No new invoices".
					showReconnectWindow();
					return;
				}
				// The range this answer is for: the modal's dateRange may already hold a newer one.
				setSelected(current => refreshSelection(current, data, range));
				setList({state: 'ready', data});
				askMatches(data.invoices);
			});
		},
		[showReconnectWindow, askMatches]
	);

	useEffect(() => {
		open.current = true;
		// Read again on every open: the token can expire after the page loaded.
		fetchXeroOrganisations().then(orgs => {
			if (!open.current) {
				return;
			}
			const reachable = (orgs || []).filter(org => !org.inaccessible);
			if (reachable.length === 0) {
				// Expired, revoked, or Xero could not be read: the existing reconnect window says
				// what to do, and nothing can be created from here.
				showReconnectWindow();
				return;
			}
			setOrganisationNames(new Map((orgs || []).map(org => [org.tenantId, org.name])));
			setOrganisations(reachable);
			loadInvoices(dateRange);
		});
		return () => {
			open.current = false;
		};
		// Once per open.
	}, []);

	const acceptDateRange = (range: InvoiceDateRange): void => {
		// The picker itself cannot be disabled (see the date field below): a range typed during a run waits.
		if (running) {
			return;
		}
		setDateRange(range);
		loadInvoices(range);
	};

	// The rows the list draws: what the header checkbox and the note act on.
	const visible: SelectableInvoice[] = useMemo(() => (list.state === 'ready' ? list.data.invoices : []), [list]);
	// Each contact's match with the selection applied: two ticked contacts can turn each other into review.
	const effective = useMemo(() => effectiveMatches(known, selected), [known, selected]);
	// The ticks as Create and the counts read them: each carrying its current match.
	const matchedSelection = useMemo(
		() => new Map(Array.from(selected, ([key, row]) => [key, decorate(row, effective)])),
		[selected, effective]
	);
	const matchOf = useCallback((row: SelectableInvoice) => matchDisplay(row, known, effective), [known, effective]);
	// Nothing can be ticked or unticked while a run is out: the count and the result must agree (D3).
	// A ticked row can always be unticked, even when its answer, asked again after a run, now makes it
	// one this user could not tick (no CLIENT_CREATE).
	const canSelect = useMemo(() => {
		if (running) {
			return () => false;
		}
		const canTick = canSelectFor(hasClientCreate, known);
		return (row: SelectableInvoice) => selected.has(invoiceKey(row)) || canTick(row);
	}, [running, hasClientCreate, known, selected]);
	const counts = selectionCounts(matchedSelection, visible);
	// A ticked contact still being asked (again, after a run) is not sent yet: Create waits for its answer
	// rather than send nothing (code review, rm#8699).
	const checkingTicked = Array.from(selected.values()).some(row => matchOf(row).state === 'checking');
	// After a run that could not import every invoice (D5); cleared by the next Create.
	const createFailure = notCreated > 0 ? formatMessage({id: 'xero_invoice_import.create_failed'}, {count: notCreated}) : '';
	// Not while the list loads: it would flash on every reload.
	const selectionNote =
		list.state !== 'loading' && counts.outsideView > 0
			? formatMessage({id: 'xero_invoice_import.selected_outside_view'}, {count: counts.outsideView})
			: '';
	// Stable, so the memoised rows re-draw only when their own tick changes.
	const toggleRow = useCallback((row: SelectableInvoice) => setSelected(current => toggleInvoice(current, row)), []);
	const toggleShown = (): void => setSelected(current => toggleVisible(current, visible, canSelect));
	const createLabel = running
		? formatMessage({id: 'xero_invoice_import.creating'})
		: counts.total === 0
		? formatMessage({id: 'xero_invoice_import.create_invoices'})
		: counts.newClients === 0
		? formatMessage({id: 'xero_invoice_import.create_invoice_count'}, {invoices: counts.total})
		: formatMessage(
				{id: 'xero_invoice_import.create_invoices_and_clients'},
				{invoices: counts.total, clients: counts.newClients}
		  );

	// Said by one region that is always there: a region that appears already holding its text is not
	// announced by many screen readers.
	const announcement =
		list.state === 'loading'
			? formatMessage({id: 'xero_invoice_import.loading_invoices'})
			: list.state === 'error'
			? [createFailure, formatMessage({id: 'xero_invoice_import.load_failed'}), selectionNote].filter(Boolean).join(' ')
			: [
					createFailure,
					...list.data.organisations
						.filter(org => org.status !== 'ok')
						.map(org => organisationMessage(org, organisationNames, isAdminUser, formatMessage)),
					selectionNote,
			  ]
					.filter(Boolean)
					.join(' ');

	/**
	 * Create: the ticked Matched invoices, 20 at a time (D1, D2). When all are in Forecast the modal
	 * closes; otherwise it stays on a fresh list, created rows gone, the rest still ticked (D5). The
	 * page reloads either way. Closing the modal during the run does not stop it: the design system's
	 * dialog closes itself on ✕ / Escape, and dropping the unsent batches would be silent (D3).
	 */
	const create = async (): Promise<void> => {
		const rows = rowsToImport(matchedSelection, known);
		if (running || rows.length === 0) {
			return;
		}
		setRunning(true);
		setNotCreated(0);
		let summary: ImportSummary;
		try {
			summary = await runImport(rows, {post: postImport});
		} catch {
			// postImport never rejects; should anything else, every invoice counts as not created.
			summary = failedSummary(rows);
		}
		const failures = notCreatedCount(summary);
		try {
			tracking.trackEvent('Import from Xero create');
			trackEvent('Xero Invoice Import', 'Created', {
				sent: summary.results.length,
				created: summary.results.length - failures,
				notCreated: failures,
			});
			onImported?.(summary);
		} catch {
			// Tracking or the page's reload failing must not leave the modal on Creating invoices….
		}
		if (!open.current) {
			return;
		}
		setRunning(false);
		// Invoices now in Forecast lose their ticks, whatever range they were ticked in: the reload only
		// drops ticks of the range it reads, and a tick left behind would be sent again.
		const inForecast = new Set(
			summary.results
				.filter(result => result.outcome === 'created' || result.outcome === 'alreadyImported')
				.map(invoiceKey)
		);
		const leftTicked = Array.from(selected.keys()).filter(key => !inForecast.has(key)).length;
		if (allDone(summary) && leftTicked === 0) {
			closeModal();
			return;
		}
		// Ticks Create did not send (New client, review) keep the modal open rather than vanish with it.
		setSelected(current => {
			const next = new Map(current);
			inForecast.forEach(key => next.delete(key));
			return next;
		});
		setNotCreated(failures);
		// A contact whose client changed, or that needs a client, is asked again with the reload, so Try
		// again sends what it matches now rather than the answer kept from before the run.
		const askAgain = new Set(
			summary.results
				.filter(result => result.outcome === 'clientChanged' || result.outcome === 'needsClient')
				.map(invoiceKey)
		);
		const staleContacts = new Set(rows.filter(row => askAgain.has(invoiceKey(row))).map(row => contactKey(row)));
		if (staleContacts.size > 0) {
			setKnown(current => {
				const next = new Map(current);
				staleContacts.forEach(key => next.delete(key));
				return next;
			});
			// Asked here, not left to the reload: the reload reads one range, and a row ticked in another
			// range would otherwise never be asked again and Try again would send nothing.
			askMatches(Array.from(selected.values()).filter(row => staleContacts.has(contactKey(row))));
		}
		loadInvoices(dateRange);
	};

	const reconnect = (): void => {
		closeModal();
		history.push(XERO_INTEGRATION_PAGE);
	};

	const organisationLabel = formatMessage({id: 'xero_invoice_import.organisation'});
	const allOrganisations = formatMessage({id: 'xero_invoice_import.all_organisations'});
	const invoiceDateLabel = formatMessage({id: 'xero_invoice_import.invoice_date'});

	return (
		<Modal size={Modal.SIZE.EXTRA_LARGE} onCloseModal={closeModal} closeOnInteractOutside={!running} setHeight>
			<Modal.Title>{formatMessage({id: 'xero_invoice_import.title'})}</Modal.Title>
			<Modal.Content>
				<Text>{formatMessage({id: 'xero_invoice_import.description'})}</Text>
				{organisations === null ? (
					<LoadingArea data-cy="xero-import-loading">
						<InlineLoader />
					</LoadingArea>
				) : (
					<FilterRow data-cy="xero-import-filters">
						<FilterField data-cy="xero-import-organisation-field">
							<Text>{organisationLabel}</Text>
							<OrganisationInput>
								{organisations.length === 1 ? (
									// One organisation: its name, read only, no dropdown.
									<TextField
										aria-label={organisationLabel}
										value={organisations[0].name}
										disabled
										width={TextField.WIDTH.FLEX}
										data-cy="xero-import-organisation"
									/>
								) : (
									// Several: All organisations. Choosing one comes later.
									<ComboBox
										aria-label={organisationLabel}
										defaultItems={[{id: ALL_ORGANISATIONS, name: allOrganisations}]}
										value={ALL_ORGANISATIONS}
										disabled
										isDisabled
										width={ComboBox.WIDTH.FLEX}
										data-cy="xero-import-organisation"
									>
										{(item: {id: string; name: string}) => (
											<ComboBox.Item key={item.id} textValue={item.name}>
												<Text>{item.name}</Text>
											</ComboBox.Item>
										)}
									</ComboBox>
								)}
							</OrganisationInput>
						</FilterField>
						<FilterField>
							<Text>{invoiceDateLabel}</Text>
							{/* While a run is out the field is inert: the DS DateRangePicker drops `disabled` (its
							    react-aria hook reads isDisabled), so clicks and focus are blocked here (D3). */}
							<DateInput
								data-cy="xero-import-date-field"
								aria-disabled={running || undefined}
								style={running ? {opacity: 0.5} : undefined}
								{...(running ? ({inert: ''} as Record<string, string>) : {})}
							>
								<ImportDateRange range={dateRange} onAccept={acceptDateRange} />
							</DateInput>
						</FilterField>
					</FilterRow>
				)}
				<VisuallyHidden role="status" data-cy="xero-import-list-status">
					{organisations === null ? '' : announcement}
				</VisuallyHidden>
				<ListArea data-cy="xero-import-list" aria-busy={organisations !== null && list.state === 'loading'}>
					{/* Plain text: the status region above says it to screen readers. */}
					{/* The DS modal footer takes only its buttons, so a Create run's failure line takes this
					    one-line slot, which never moves the list, until the next Create. The selection note
					    is still said by the status region meanwhile. */}
					<SelectionNote data-cy="xero-import-selection-note">
						{createFailure ? (
							<span data-cy="xero-import-create-failed">
								<Detail color="error">{createFailure}</Detail>
							</span>
						) : selectionNote ? (
							<Detail color="medium">{selectionNote}</Detail>
						) : null}
					</SelectionNote>
					{organisations === null ? null : (
						<UnlinkedInvoiceList
							list={list}
							organisationNames={organisationNames}
							isAdmin={isAdminUser}
							onRetry={() => loadInvoices(dateRange)}
							onReconnect={reconnect}
							selected={selected}
							headerState={headerState(selected, visible, canSelect)}
							hasSelection={counts.total > 0}
							canSelect={canSelect}
							onToggle={toggleRow}
							onToggleVisible={toggleShown}
							matchOf={matchOf}
							canCreateClients={hasClientCreate}
						/>
					)}
				</ListArea>
			</Modal.Content>
			<Modal.Footer>
				<Modal.SecondaryFooterButton onPress={closeModal} disabled={running} dataCy="xero-import-cancel">
					{formatMessage({id: 'common.cancel'})}
				</Modal.SecondaryFooterButton>
				{/* Enabled with something ticked; creates the ticked invoices matched to a client. */}
				<Modal.PrimaryFooterButton
					disabled={counts.total === 0 || running || checkingTicked}
					onPress={create}
					dataCy="xero-import-create"
				>
					{createLabel}
				</Modal.PrimaryFooterButton>
			</Modal.Footer>
		</Modal>
	);
};

export default XeroInvoiceImportModal;
